import './App.css';
import { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation
} from 'react-router-dom';
import AdminDashboard from './satsuki-admin-dashboard';
import EmployeeAttendanceTool from './satsuki-employee';
import { AuthProvider } from './auth/AuthProvider';
import PrivateRoute from './auth/PrivateRoute';
import Login from './pages/Login';
import PasswordResetRequest from './pages/PasswordResetRequest';
import PasswordResetUpdate from './pages/PasswordResetUpdate';
import AuthCallback from './pages/AuthCallback';
import ShiftRequest from './pages/ShiftRequest';
import CareHome from './pages/CareHome';
import InventoryPage from './inventory/InventoryPage';
import MedicinePage from './medicine/MedicinePage';
import ResidentEntryPage from './residents/ResidentEntryPage';
import EmergencyInfoPage from './residents/EmergencyInfoPage';
import ResidentManagementPage from './residents/ResidentManagementPage';
import SupplySalesPage from './supplies/SupplySalesPage';

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname === '/' && window.location.hash) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      if (params.has('access_token') || params.has('refresh_token') || params.has('code')) {
        navigate(`/auth/callback${hash}`, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // 管理者ページは独自のヘッダー/フッターを持つため、レイアウトなしで表示
  if (isAdminPage) {
    return <Outlet />;
  }

  // 従業員ページのみシンプルなレイアウトで表示
  return (
    <div className="App">
      <div className="flex flex-col min-h-screen">
        <header className="bg-gray-800 text-white p-4">
          <div className="container mx-auto">
            <h1 className="text-xl font-bold">勤怠管理システム</h1>
          </div>
        </header>

        <main className="flex-grow">
          <Outlet />
        </main>

        <footer className="bg-gray-800 text-white p-4 text-center">
          <p>© 2025 勤怠管理システム</p>
        </footer>
      </div>
    </div>
  );
};

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/password-reset" element={<PasswordResetRequest />} />
        <Route path="/password-reset/update" element={<PasswordResetUpdate />} />
        {/* アカウント作成は管理者がSupabaseで行うため、公開の登録画面は廃止 */}
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/shift-request" element={<ShiftRequest />} />
        <Route path="/care" element={<CareHome />} />
        <Route path="/inventory" element={<InventoryPage storageMode="supabase" />} />
        <Route path="/inventory-preview" element={<InventoryPage storageMode="local" />} />
        <Route path="/resident-entry" element={<ResidentEntryPage />} />
        <Route path="/" element={<AppLayout />}>
          <Route index element={<EmployeeAttendanceTool />} />
          {/* ケア業務（パスコードで開く現場向け画面） */}
          <Route path="admin/medicine" element={<MedicinePage />} />
          <Route path="admin/residents" element={<ResidentManagementPage />} />
          <Route path="admin/emergency" element={<EmergencyInfoPage />} />
          <Route path="admin/supplies" element={<SupplySalesPage />} />
          {/* 労務（勤怠・給与・行政報告）は管理者のみ */}
          <Route element={<PrivateRoute requireRoles={['admin', 'shift']} />}>
            <Route path="admin" element={<AdminDashboard />} />
          </Route>
          <Route element={<PrivateRoute requireRoles={['admin']} />}>
            <Route path="admin/inventory" element={<InventoryPage storageMode="supabase" />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </AuthProvider>
);

export default App;
